import { Injectable, isDevMode } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

/** What every tab agrees on about the current login. */
export interface AuthSharedState {
  /** Identifies the login. Server `SessionId` once the API sends one; a client UUID until then. */
  sessionId: string;
  /** Bumped only when the credentials themselves change: login, refresh, adopting another tab's refresh. */
  credentialRevision: number;
  accessTokenExpiresAt: string | null;
  refreshTokenExpiresAt: string | null;
  accessTokenIssuedAt: string | null;
}

const STORAGE_KEY = 'marina_auth_state';

/**
 * The identity and revision of the current login, shared across tabs.
 *
 * Two questions need answering all over the auth layer, and neither can be answered from a timestamp:
 *
 *  - *Is this response still relevant?* A refresh or a 401 belonging to a login the user has since
 *    replaced must not touch the new one. Comparing `sessionId` answers it; comparing expiry
 *    timestamps does not, because two logins can produce identical ones.
 *  - *Has somebody already fixed this?* A 401 that was sent before another tab refreshed needs a
 *    plain retry, not a second rotation. Comparing `credentialRevision` answers it.
 *
 * The revision is deliberately **not** bumped by `init()` or by restoring from cache: those change
 * what we know about the user, not the credentials in the jar. Bumping there would make a genuine
 * 401 look like "somebody already refreshed", and the retry would fail for the same reason twice.
 */
@Injectable({ providedIn: 'root' })
export class AuthSessionStateService {
  private readonly stateSubject = new BehaviorSubject<AuthSharedState | null>(null);
  readonly state$: Observable<AuthSharedState | null> = this.stateSubject.asObservable();

  private readonly clearedElsewhereSubject = new Subject<void>();
  /**
   * Fires when *another tab* removed the shared state — i.e. ended the session.
   *
   * Distinct from `state$` emitting null, which also happens when storage is simply unreadable
   * (private browsing, storage disabled). The `storage` event is the one unambiguous signal: it is
   * only delivered to tabs other than the writer, and `newValue === null` means the key was removed.
   */
  readonly clearedElsewhere$: Observable<void> = this.clearedElsewhereSubject.asObservable();

  constructor() {
    this.stateSubject.next(this.read());
    this.listenForOtherTabs();
  }

  get snapshot(): AuthSharedState | null {
    // Always re-read: another tab may have written since our last storage event, and the lock
    // holder's whole job is to act on what is true *now*, not what it was told a moment ago.
    const stored = this.read();
    if (!this.equal(stored, this.stateSubject.value)) {
      this.stateSubject.next(stored);
    }
    return stored;
  }

  get sessionId(): string | null {
    return this.snapshot?.sessionId ?? null;
  }

  get credentialRevision(): number {
    return this.snapshot?.credentialRevision ?? 0;
  }

  /** True when `sessionId` is the login currently in the jar. A null id never matches. */
  isCurrent(sessionId: string | null | undefined): boolean {
    if (!sessionId) {
      return false;
    }
    return this.snapshot?.sessionId === sessionId;
  }

  /** True when credentials for this same login advanced past `revision` — someone else refreshed. */
  revisionAdvancedSince(sessionId: string | null | undefined, revision: number): boolean {
    const current = this.snapshot;
    if (!current || !sessionId || current.sessionId !== sessionId) {
      return false;
    }
    return current.credentialRevision > revision;
  }

  /** Begin a new login. Returns the new state, including the id every later check compares against. */
  startSession(sessionId: string | null, expirations: Partial<AuthSharedState> = {}): AuthSharedState {
    const next: AuthSharedState = {
      sessionId: sessionId || this.newClientSessionId(),
      credentialRevision: 1,
      accessTokenExpiresAt: expirations.accessTokenExpiresAt ?? null,
      refreshTokenExpiresAt: expirations.refreshTokenExpiresAt ?? null,
      accessTokenIssuedAt: expirations.accessTokenIssuedAt ?? null,
    };
    this.write(next);
    return next;
  }

  /**
   * Record that credentials changed for the current login, bumping the revision other tabs compare
   * against. Ignored when the login has already been replaced — a late refresh must not resurrect it.
   */
  recordCredentialChange(sessionId: string | null, expirations: Partial<AuthSharedState>): AuthSharedState | null {
    const current = this.snapshot;
    if (!current) {
      return null;
    }
    if (sessionId && current.sessionId !== sessionId) {
      this.log('[AuthSessionState] Dropping credential change for a session that is no longer current');
      return null;
    }

    const next: AuthSharedState = {
      ...current,
      credentialRevision: current.credentialRevision + 1,
      accessTokenExpiresAt: expirations.accessTokenExpiresAt ?? current.accessTokenExpiresAt,
      refreshTokenExpiresAt: expirations.refreshTokenExpiresAt ?? current.refreshTokenExpiresAt,
      accessTokenIssuedAt: expirations.accessTokenIssuedAt ?? current.accessTokenIssuedAt,
    };
    this.write(next);
    return next;
  }

  /** Forget the login. Called when a session ends, from any cause. */
  clear(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Private browsing / storage disabled: in-memory state below is still authoritative for this tab.
    }
    this.stateSubject.next(null);
  }

  private newClientSessionId(): string {
    try {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
      }
    } catch {
      // fall through
    }
    return `sid-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  private read(): AuthSharedState | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as Partial<AuthSharedState>;
      if (!parsed?.sessionId || typeof parsed.credentialRevision !== 'number') {
        return null;
      }
      return {
        sessionId: parsed.sessionId,
        credentialRevision: parsed.credentialRevision,
        accessTokenExpiresAt: parsed.accessTokenExpiresAt ?? null,
        refreshTokenExpiresAt: parsed.refreshTokenExpiresAt ?? null,
        accessTokenIssuedAt: parsed.accessTokenIssuedAt ?? null,
      };
    } catch {
      return null;
    }
  }

  private write(state: AuthSharedState): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Storage unavailable — this tab still behaves correctly, it just cannot tell the others.
    }
    this.stateSubject.next(state);
  }

  private listenForOtherTabs(): void {
    if (typeof window === 'undefined') {
      return;
    }
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) {
        return;
      }
      const next = this.read();
      if (!this.equal(next, this.stateSubject.value)) {
        this.stateSubject.next(next);
      }
      if (event.newValue === null) {
        this.clearedElsewhereSubject.next();
      }
    });
  }

  private equal(a: AuthSharedState | null, b: AuthSharedState | null): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    return a.sessionId === b.sessionId && a.credentialRevision === b.credentialRevision;
  }

  private log(...args: unknown[]): void {
    if (isDevMode()) {
      console.log(...args);
    }
  }
}
